/**
 * Sections & Tables Page
 * Main page component for managing outlet sections and tables
 */

import { DriverHook } from 'driver.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BusinessType } from '@shared/types';
import { usePowerSyncChange } from '@/renderer/lib/powersync/ipcBridge';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import { usePrinterConfiguration } from '../../../hooks/printers/usePrinterConfiguration';
import { selectCanManageSectionsTables } from '../../../store/auth/authSelectors';
import {
  selectEffectiveOutlet,
  selectEffectiveOutletId,
} from '../../../store/outlets';
import {
  fetchSections,
  fetchTables,
} from '../../../store/sections/sectionThunks';
import { OutletSection } from '../../../types/section';
import { useOnboardingDriverTour } from '../../../hooks/useOnboardingDriverTour';
import {
  createTourElementResolver,
  getQueuedOnboardingTour,
  ONBOARDING_TOUR_TARGETS,
  onboardingTourSelector,
} from '../../../services/onboardingTourService';
import { OutletIndicator, ReadOnlyAccessNotice, TabButton } from '../../common';
import { SectionsTab } from './SectionsTab';
import { TablesTab } from './TablesTab';

// Printers and printer routing live under Settings → Printer Settings.
type ActiveTab = 'sections' | 'tables';

const RESTAURANT_TABS: { key: ActiveTab; label: string }[] = [
  { key: 'sections', label: 'Sections' },
  { key: 'tables', label: 'Tables' },
];

const NON_RESTAURANT_TABS: { key: ActiveTab; label: string }[] = [
  { key: 'tables', label: 'Tables' },
];

export const SectionsAndTablesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const outletId = useAppSelector(selectEffectiveOutletId);
  const selectedOutlet = useAppSelector(selectEffectiveOutlet);
  const canManageSectionsTables = useAppSelector(selectCanManageSectionsTables);
  const { hasPrinterConfigForSection } = usePrinterConfiguration();
  const [activeTab, setActiveTab] = useState<ActiveTab>('sections');
  const queuedTourStep = getQueuedOnboardingTour();
  const isRestaurantOutlet = selectedOutlet
    ? selectedOutlet.business_type === BusinessType.RESTAURANT_BAR
    : true;
  const visibleTabs = isRestaurantOutlet
    ? RESTAURANT_TABS
    : NON_RESTAURANT_TABS;

  const moveToTablesTourStep = useCallback<DriverHook>(
    (_element, _step, options) => {
      setActiveTab('tables');
      window.setTimeout(() => {
        options.driver.moveNext();
      }, 250);
    },
    [],
  );

  useEffect(() => {
    dispatch(fetchSections(outletId));
    dispatch(fetchTables(outletId));
  }, [dispatch, outletId]);

  // Reactively refresh sections/tables when local PowerSync SQLite changes
  // (e.g. an edit made from this page, or synced in from another device).
  // Without this, this page only ever refetches on mount/outlet-change, so
  // edits appear stale here until a full remount (previously required
  // signing out and back in).
  usePowerSyncChange(['outlet_sections', 'outlet_tables'], () => {
    dispatch(fetchSections(outletId));
    dispatch(fetchTables(outletId));
  });

  useEffect(() => {
    if (visibleTabs.some((tab) => tab.key === activeTab)) return;

    setActiveTab(visibleTabs[0]?.key || 'tables');
  }, [activeTab, visibleTabs]);

  useEffect(() => {
    if (queuedTourStep === 'sections_tables' && activeTab !== 'sections') {
      setActiveTab('sections');
    }
  }, [activeTab, queuedTourStep]);

  // Navigate to tables tab when clicking "VIEW" on a section row
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleViewTables = useCallback((_section: OutletSection) => {
    setActiveTab('tables');
  }, []);

  const handleOpenPrinterSetup = useCallback(
    (section: OutletSection) => {
      navigate(`/settings/printers/configuration?section=${section.id}`);
    },
    [navigate],
  );

  const sectionsTablesTourSteps = useMemo(
    () => [
      {
        element: onboardingTourSelector(ONBOARDING_TOUR_TARGETS.sectionsPage),
        popover: {
          title: 'Sections and tables setup',
          description:
            'This page is where you build the dining layout your staff will use to open orders.',
          side: 'bottom' as const,
        },
      },
      {
        element: createTourElementResolver([
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.sectionsAddButton),
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.sectionsTable),
        ]),
        popover: {
          title: 'Create your sections first',
          description:
            'Start with the Sections tab and add the dining areas your outlet uses, like terrace, bar, or main hall.',
          side: 'left' as const,
        },
      },
      {
        element: onboardingTourSelector(
          ONBOARDING_TOUR_TARGETS.sectionsTabTables,
        ),
        popover: {
          title: 'Then move to tables',
          description:
            'After creating sections, open the Tables tab and create the actual tables under those sections.',
          side: 'bottom' as const,
          onNextClick: moveToTablesTourStep,
        },
      },
      {
        element: createTourElementResolver([
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.tablesAddButton),
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.tablesTable),
        ]),
        popover: {
          title: 'Add tables for each section',
          description:
            'Use this action to create the actual tables that waiters will select later on the Sales page.',
          side: 'left' as const,
        },
      },
    ],
    [moveToTablesTourStep],
  );

  useOnboardingDriverTour({
    isEnabled: queuedTourStep === 'sections_tables',
    stepId: 'sections_tables',
    steps: sectionsTablesTourSteps,
  });

  const renderTabContent = () => {
    switch (activeTab) {
      case 'sections':
        return (
          <SectionsTab
            canManage={canManageSectionsTables}
            hasPrinterConfigForSection={hasPrinterConfigForSection}
            onConfigurePrinters={handleOpenPrinterSetup}
            onViewTables={handleViewTables}
          />
        );
      case 'tables':
        return <TablesTab canManage={canManageSectionsTables} />;
      default:
        return null;
    }
  };

  return (
    <div
      className="space-y-6"
      data-onboarding-tour={ONBOARDING_TOUR_TARGETS.sectionsPage}
    >
      <OutletIndicator description="Viewing sections and tables across all outlets. Select a specific outlet to create new sections and tables." />

      {!canManageSectionsTables ? (
        <ReadOnlyAccessNotice message="You can view sections and tables here, but only authorized roles can change them." />
      ) : null}

      {/* Page Header */}
      {/* <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Sections &amp; Tables
        </h1>
      </div> */}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6" aria-label="Sections and Tables tabs">
          {visibleTabs.map((tab) => (
            <TabButton
              key={tab.key}
              label={tab.label}
              isActive={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
              dataTourId={
                tab.key === 'sections'
                  ? ONBOARDING_TOUR_TARGETS.sectionsTabSections
                  : ONBOARDING_TOUR_TARGETS.sectionsTabTables
              }
            />
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};
